import { orderFrameIdsBySequenceAndTime } from "./frameOrdering";
import type {
  AnnotationFrameDraft,
  AnnotationSetDraft,
  AnnotationStudioInitialSession,
  AnnotationStudioViewMode,
  AnnotationWorkspaceSnapshot,
  ReplayableRenderSourceDescriptor,
} from "./types";

export interface HydratedAnnotationStudioSession {
  readonly draft: AnnotationSetDraft;
  readonly selectedSequenceId: string | null;
  readonly selectedStreamId: string | null;
  readonly selectedTargetId: string | null;
  readonly viewMode: AnnotationStudioViewMode;
  readonly descriptors: readonly ReplayableRenderSourceDescriptor[];
}

function invalidInitialSession(reason: string): never {
  throw new Error(`Invalid AnnotationStudioInitialSession: ${reason}`);
}

function assertFinite(value: number, field: string): void {
  if (!Number.isFinite(value)) invalidInitialSession(`${field} must be finite.`);
}

function assertNormalized(value: number, field: string): void {
  assertFinite(value, field);
  if (value < 0 || value > 1) {
    invalidInitialSession(`${field} must be within the inclusive [0, 1] range.`);
  }
}

function assertDescriptor(descriptor: ReplayableRenderSourceDescriptor, field: string): void {
  if (descriptor.id.length === 0) invalidInitialSession(`${field}.id must not be empty.`);
  assertFinite(descriptor.revision, `${field}.revision`);
}

function resolveInitialStreamId(
  workspace: AnnotationWorkspaceSnapshot,
  sequenceId: string | null,
  preferredStreamId: string | null | undefined,
): string | null {
  if (sequenceId === null) return null;
  const sequence = workspace.sequences.find((candidate) => candidate.id === sequenceId);
  if (preferredStreamId !== undefined && preferredStreamId !== null) {
    if (
      !sequence?.streamIds.includes(preferredStreamId) ||
      !workspace.streams.some((stream) => stream.id === preferredStreamId)
    ) {
      invalidInitialSession(
        `selectedStreamId "${preferredStreamId}" does not belong to selectedSequenceId "${sequenceId}".`,
      );
    }
    return preferredStreamId;
  }
  return (
    sequence?.streamIds.find((streamId) =>
      workspace.streams.some((stream) => stream.id === streamId),
    ) ?? null
  );
}

export function hydrateAnnotationStudioInitialSession(
  workspace: AnnotationWorkspaceSnapshot,
  session: AnnotationStudioInitialSession,
): HydratedAnnotationStudioSession {
  if (session.collectionId !== workspace.collectionId) {
    invalidInitialSession(
      `collectionId "${session.collectionId}" does not match workspace "${workspace.collectionId}".`,
    );
  }
  if (session.workspaceRevision !== workspace.revision) {
    invalidInitialSession(
      `workspaceRevision ${session.workspaceRevision} does not match workspace revision ${workspace.revision}.`,
    );
  }
  if (session.draftId.length === 0) invalidInitialSession("draftId must not be empty.");

  if (session.base) {
    const savedSet = workspace.annotationSets.find((set) => set.setId === session.base?.setId);
    if (!savedSet) {
      invalidInitialSession(`base set "${session.base.setId}" is absent from the workspace.`);
    }
    if (savedSet.revision !== session.base.revision) {
      invalidInitialSession(
        `base revision ${session.base.revision} does not match saved set revision ${savedSet.revision}.`,
      );
    }
  }

  const sequenceIds = new Set(workspace.sequences.map((sequence) => sequence.id));
  const streamIds = new Set(workspace.streams.map((stream) => stream.id));
  const targetIds = new Set(workspace.targets.map((target) => target.id));
  const seenFrameIds = new Set<string>();
  const descriptors: ReplayableRenderSourceDescriptor[] = [];
  const frames: Record<string, AnnotationFrameDraft> = {};

  for (const initialFrame of session.orderedFrames) {
    if (initialFrame.frameId.length === 0) invalidInitialSession("frameId must not be empty.");
    if (seenFrameIds.has(initialFrame.frameId)) {
      invalidInitialSession(`frameId "${initialFrame.frameId}" is duplicated.`);
    }
    seenFrameIds.add(initialFrame.frameId);

    if (!sequenceIds.has(initialFrame.sequenceId)) {
      invalidInitialSession(
        `frame "${initialFrame.frameId}" references unknown sequence "${initialFrame.sequenceId}".`,
      );
    }
    if (!streamIds.has(initialFrame.streamId)) {
      invalidInitialSession(
        `frame "${initialFrame.frameId}" references unknown stream "${initialFrame.streamId}".`,
      );
    }
    const sequence = workspace.sequences.find(
      (candidate) => candidate.id === initialFrame.sequenceId,
    );
    if (!sequence?.streamIds.includes(initialFrame.streamId)) {
      invalidInitialSession(
        `stream "${initialFrame.streamId}" does not belong to sequence "${initialFrame.sequenceId}".`,
      );
    }

    assertFinite(initialFrame.timestampSeconds, `frame "${initialFrame.frameId}".timestampSeconds`);
    if (initialFrame.timestampSeconds < 0) {
      invalidInitialSession(
        `frame "${initialFrame.frameId}".timestampSeconds must not be negative.`,
      );
    }
    assertFinite(initialFrame.sourceRevision, `frame "${initialFrame.frameId}".sourceRevision`);
    assertFinite(initialFrame.width, `frame "${initialFrame.frameId}".width`);
    assertFinite(initialFrame.height, `frame "${initialFrame.frameId}".height`);
    if (initialFrame.width <= 0 || initialFrame.height <= 0) {
      invalidInitialSession(`frame "${initialFrame.frameId}" dimensions must be positive.`);
    }
    assertDescriptor(initialFrame.frameSource, `frame "${initialFrame.frameId}".frameSource`);
    descriptors.push(initialFrame.frameSource);

    const frameTargets = Object.fromEntries(
      Object.entries(initialFrame.targets).map(([targetKey, initialTarget]) => {
        if (targetKey !== initialTarget.targetId) {
          invalidInitialSession(
            `frame "${initialFrame.frameId}" target key "${targetKey}" does not match targetId "${initialTarget.targetId}".`,
          );
        }
        if (!targetIds.has(initialTarget.targetId)) {
          invalidInitialSession(
            `frame "${initialFrame.frameId}" references unknown target "${initialTarget.targetId}".`,
          );
        }
        if (
          initialTarget.pointRevision !== undefined &&
          (!Number.isInteger(initialTarget.pointRevision) || initialTarget.pointRevision < 0)
        ) {
          invalidInitialSession(
            `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" pointRevision must be a non-negative integer.`,
          );
        }

        const pointIds = new Set<string>();
        for (const point of initialTarget.points) {
          if (point.id.length === 0) {
            invalidInitialSession(
              `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" has an empty point id.`,
            );
          }
          if (pointIds.has(point.id)) {
            invalidInitialSession(
              `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" duplicates point "${point.id}".`,
            );
          }
          pointIds.add(point.id);
          assertNormalized(
            point.x,
            `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" point "${point.id}".x`,
          );
          assertNormalized(
            point.y,
            `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" point "${point.id}".y`,
          );
        }

        for (const [contourIndex, contour] of (initialTarget.contours ?? []).entries()) {
          for (const [pointIndex, point] of contour.entries()) {
            assertNormalized(
              point.x,
              `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" contour ${contourIndex} point ${pointIndex}.x`,
            );
            assertNormalized(
              point.y,
              `frame "${initialFrame.frameId}" target "${initialTarget.targetId}" contour ${contourIndex} point ${pointIndex}.y`,
            );
          }
        }

        if (initialTarget.maskSource) {
          assertDescriptor(
            initialTarget.maskSource,
            `frame "${initialFrame.frameId}" target "${initialTarget.targetId}".maskSource`,
          );
          descriptors.push(initialTarget.maskSource);
        }

        return [
          targetKey,
          {
            targetId: initialTarget.targetId,
            points: initialTarget.points,
            pointRevision: initialTarget.pointRevision ?? 0,
            contours: initialTarget.contours,
            maskSource: initialTarget.maskSource
              ? {
                  id: initialTarget.maskSource.id,
                  revision: initialTarget.maskSource.revision,
                }
              : undefined,
          },
        ];
      }),
    );

    frames[initialFrame.frameId] = {
      frameId: initialFrame.frameId,
      sequenceId: initialFrame.sequenceId,
      streamId: initialFrame.streamId,
      timestampSeconds: initialFrame.timestampSeconds,
      sourceRevision: initialFrame.sourceRevision,
      width: initialFrame.width,
      height: initialFrame.height,
      frameSource: {
        id: initialFrame.frameSource.id,
        revision: initialFrame.frameSource.revision,
      },
      targets: frameTargets,
    };
  }

  if (session.activeFrameId !== null && !frames[session.activeFrameId]) {
    invalidInitialSession(`activeFrameId "${session.activeFrameId}" is absent from orderedFrames.`);
  }

  const activeFrame = session.activeFrameId === null ? undefined : frames[session.activeFrameId];
  const selectedSequenceId =
    session.selectedSequenceId === undefined
      ? (activeFrame?.sequenceId ?? workspace.sequences[0]?.id ?? null)
      : session.selectedSequenceId;
  if (
    selectedSequenceId !== null &&
    !workspace.sequences.some((sequence) => sequence.id === selectedSequenceId)
  ) {
    invalidInitialSession(`selectedSequenceId "${selectedSequenceId}" is absent from workspace.`);
  }
  const selectedStreamId = resolveInitialStreamId(
    workspace,
    selectedSequenceId,
    session.selectedStreamId === undefined && activeFrame?.sequenceId === selectedSequenceId
      ? activeFrame.streamId
      : session.selectedStreamId,
  );
  const selectedTargetId =
    session.selectedTargetId === undefined
      ? (workspace.targets[0]?.id ?? null)
      : session.selectedTargetId;
  if (
    selectedTargetId !== null &&
    !workspace.targets.some((target) => target.id === selectedTargetId)
  ) {
    invalidInitialSession(`selectedTargetId "${selectedTargetId}" is absent from workspace.`);
  }

  const viewMode = session.viewMode ?? "browse";
  if (viewMode === "annotate") {
    if (!activeFrame) {
      invalidInitialSession("viewMode annotate requires an activeFrameId.");
    }
    if (
      selectedSequenceId !== activeFrame.sequenceId ||
      selectedStreamId !== activeFrame.streamId
    ) {
      invalidInitialSession(
        "viewMode annotate requires selected sequence and stream to match the active frame.",
      );
    }
  }

  const orderedFrameIds = orderFrameIdsBySequenceAndTime(
    session.orderedFrames.map((frame) => frame.frameId),
    frames,
    workspace.sequences.map((sequence) => sequence.id),
  );
  return {
    draft: {
      draftId: session.draftId,
      base: session.base,
      orderedFrameIds,
      frames,
      activeFrameId: session.activeFrameId,
      dirtyScopes: [],
    },
    selectedSequenceId,
    selectedStreamId,
    selectedTargetId,
    viewMode,
    descriptors,
  };
}
